(() => {
  const normalize = (value) => (value == null ? '' : String(value).trim().toLowerCase());
  const HIDDEN_ATTRIBUTE = 'data-color-media-hidden';

  function initialize(configElement) {
    if (configElement.dataset.inited === '1') return;

    let config;
    try {
      config = JSON.parse(configElement.textContent || '{}');
    } catch (_error) {
      return;
    }

    const sectionId = config.sectionId;
    const colorOptionIndex = Number(config.colorOptionIndex);
    const separator = config.sep || '␟';
    const markerPositionByKey = config.markerPosByKey || {};

    if (!sectionId || !Number.isFinite(colorOptionIndex)) return;
    configElement.dataset.inited = '1';

    const sectionRoot = () => document.getElementById(`shopify-section-${sectionId}`);
    const variantPicker = () => sectionRoot()?.querySelector('variant-selects');
    const mediaGallery = () => sectionRoot()?.querySelector(`#MediaGallery-${CSS.escape(String(sectionId))}`);
    const gallerySelector = `#Slider-Gallery-${sectionId}`;
    const thumbnailSelector = `#Slider-Thumbnails-${sectionId}`;

    function selectedValueAt(index) {
      const picker = variantPicker();
      if (!picker) return '';

      const optionControls = Array.from(
        picker.querySelectorAll('fieldset, .product-form__input--dropdown')
      );
      const control = optionControls[index];
      if (!control) return '';

      const selectedInput = control.querySelector('input:checked');
      if (selectedInput?.value != null) return selectedInput.value;

      const selectedOption = control.querySelector('select option:checked, select option[selected]');
      if (selectedOption?.value != null) return selectedOption.value;

      const selectedLabel = control.querySelector('[data-selected-value]');
      return selectedLabel?.textContent || '';
    }

    function selectedContextKey() {
      const picker = variantPicker();
      if (!picker) return '';

      const optionControls = Array.from(
        picker.querySelectorAll('fieldset, .product-form__input--dropdown')
      );
      const values = optionControls.map((_control, index) =>
        index === colorOptionIndex ? '' : normalize(selectedValueAt(index))
      );
      return values.join(separator);
    }

    function galleryState() {
      const gallery = mediaGallery();
      if (!gallery) return null;

      const slides = Array.from(
        gallery.querySelectorAll(`${gallerySelector} [data-media-id][data-real-position]`)
      );
      const thumbnails = Array.from(
        gallery.querySelectorAll(`${thumbnailSelector} [data-target]`)
      );
      const positionByMediaId = new Map();

      slides.forEach((slide) => {
        const mediaId = slide.getAttribute('data-media-id');
        const position = Number(slide.getAttribute('data-real-position'));
        if (mediaId && Number.isFinite(position)) positionByMediaId.set(mediaId, position);
      });

      return { gallery, slides, thumbnails, positionByMediaId };
    }

    const markerPositions = [...new Set(
      Object.values(markerPositionByKey).map(Number).filter(Number.isFinite)
    )].sort((a, b) => a - b);

    function markerFor(contextKey, colorValue) {
      const exactMarker = Number(markerPositionByKey[`${contextKey}||${colorValue}`]);
      if (Number.isFinite(exactMarker)) return exactMarker;

      let fallbackMarker = null;
      Object.entries(markerPositionByKey).forEach(([key, positionValue]) => {
        const keyParts = String(key).split('||');
        const position = Number(positionValue);
        if (keyParts.length !== 2 || keyParts[1] !== colorValue || !Number.isFinite(position)) return;
        if (fallbackMarker == null || position < fallbackMarker) fallbackMarker = position;
      });
      return fallbackMarker;
    }

    function groupRange(marker) {
      const markerIndex = markerPositions.indexOf(marker);
      if (markerIndex === -1) return null;
      return {
        start: marker,
        end: markerIndex < markerPositions.length - 1
          ? markerPositions[markerIndex + 1] - 1
          : Number.POSITIVE_INFINITY,
      };
    }

    function setMediaVisible(element, visible) {
      element.hidden = !visible;
      if (visible) {
        element.removeAttribute(HIDDEN_ATTRIBUTE);
        element.removeAttribute('aria-hidden');
      } else {
        element.setAttribute(HIDDEN_ATTRIBUTE, 'true');
        element.setAttribute('aria-hidden', 'true');
      }
    }

    function showAll(state) {
      state.slides.forEach((slide) => setMediaVisible(slide, true));
      state.thumbnails.forEach((thumbnail) => setMediaVisible(thumbnail, true));
      resetSliders(state.gallery);
    }

    function resetSliders(gallery) {
      gallery.querySelectorAll('slider-component').forEach((slider) => slider.resetPages?.());
    }

    function applyFilter() {
      const state = galleryState();
      if (!state) return;

      const colorValue = normalize(selectedValueAt(colorOptionIndex));
      if (!colorValue || markerPositions.length === 0) {
        showAll(state);
        return;
      }

      const marker = markerFor(selectedContextKey(), colorValue);
      const range = Number.isFinite(marker) ? groupRange(marker) : null;
      if (!range) {
        showAll(state);
        return;
      }

      state.slides.forEach((slide) => {
        const position = Number(slide.getAttribute('data-real-position'));
        setMediaVisible(slide, Number.isFinite(position) && position >= range.start && position <= range.end);
      });

      state.thumbnails.forEach((thumbnail) => {
        const mediaId = thumbnail.getAttribute('data-target');
        const position = state.positionByMediaId.get(mediaId);
        setMediaVisible(
          thumbnail,
          Number.isFinite(position) && position >= range.start && position <= range.end
        );
      });

      const activeSlide = state.gallery.querySelector(`${gallerySelector} .is-active`);
      const firstVisibleSlide = state.slides.find((slide) => !slide.hidden);
      const firstVisibleThumbnail = state.thumbnails.find((thumbnail) => !thumbnail.hidden);

      if ((!activeSlide || activeSlide.hidden) && firstVisibleSlide) {
        state.gallery.setActiveMedia?.(firstVisibleSlide.getAttribute('data-media-id'), false);
      } else if (firstVisibleThumbnail) {
        const activeThumbnail = state.gallery.querySelector(`${thumbnailSelector} [aria-current="true"]`);
        if (!activeThumbnail || activeThumbnail.closest('[data-target]')?.hidden) {
          firstVisibleThumbnail.querySelector('button')?.click();
        }
      }

      resetSliders(state.gallery);
    }

    let scheduledFrame;
    function scheduleFilter() {
      cancelAnimationFrame(scheduledFrame);
      scheduledFrame = requestAnimationFrame(() => requestAnimationFrame(applyFilter));
    }

    scheduleFilter();

    const root = sectionRoot();
    root?.addEventListener('change', scheduleFilter, true);
    root?.addEventListener('click', scheduleFilter, true);

    const gallery = mediaGallery();
    if (gallery) {
      new MutationObserver((mutations) => {
        if (mutations.some((mutation) => mutation.type === 'childList')) scheduleFilter();
      }).observe(gallery, { childList: true, subtree: true });
    }
  }

  function initializeAll() {
    document
      .querySelectorAll('script[type="application/json"][id^="MediaGroupByContextColor-"]')
      .forEach(initialize);
  }

  document.addEventListener('DOMContentLoaded', initializeAll);
  document.addEventListener('shopify:section:load', initializeAll);

  new MutationObserver((mutations) => {
    if (mutations.some((mutation) =>
      Array.from(mutation.addedNodes || []).some((node) =>
        node instanceof HTMLElement && (
          node.matches?.('script[type="application/json"][id^="MediaGroupByContextColor-"]') ||
          node.querySelector?.('script[type="application/json"][id^="MediaGroupByContextColor-"]')
        )
      )
    )) initializeAll();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
